import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  appendThreadMessage,
  createBillingCheckoutSession,
  createBillingPortalSession,
  getMyBillingSubscription,
  notifyAdmin,
  submitStoreAccessRequest,
  supabase,
  syncBillingFromCheckoutSession,
  uploadSupportAttachment,
} from '../supabaseClient';
import { INDUSTRY_OPTIONS } from '../constants/industryOptions';
import { fetchPublicSiteContent } from '../supabaseClient';
import { buildPublicSiteContentMap } from '../publicSiteContent';
import StoreRequestForm from './StoreRequestForm';
import SupportMessageModal from './SupportMessageModal';
import { parseSupportMessageContent } from '../supportMessageContent';
import './StoreOwnerPortal.css';

// ── helpers ────────────────────────────────────────────────────────────────
const parseContent = (raw = '') => parseSupportMessageContent(raw);
const getAttachmentList = (attachments = []) => attachments.filter((attachment) => attachment?.url);
const triggerBrowserDownload = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'attachment';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
const downloadAttachment = async (attachment) => {
  if (!attachment?.url) return;
  try {
    const response = await fetch(attachment.url);
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    triggerBrowserDownload(blobUrl, attachment.fileName || 'attachment');
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    triggerBrowserDownload(attachment.url, attachment.fileName || 'attachment');
  }
};
const downloadAttachments = (attachments = []) => {
  getAttachmentList(attachments).forEach((attachment, index) => {
    window.setTimeout(() => {
      downloadAttachment({
        ...attachment,
        fileName: attachment.fileName || `attachment-${index + 1}`,
      });
    }, index * 120);
  });
};
const collectMessageAttachments = (parsedMessage, parsedReply, thread) => {
  const seen = new Set();
  return [
    ...getAttachmentList(parsedMessage?.attachments),
    ...getAttachmentList(parsedReply?.attachments),
    ...thread.flatMap((entry) => getAttachmentList(entry.attachments || [])),
  ].filter((attachment) => {
    const key = `${attachment.url}|${attachment.fileName || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '';

// ── helpers (wizard) ─────────────────────────────────────────────────────
const getAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age -= 1;
  return age;
};

// ── Step indicator ────────────────────────────────────────────────────────
const WIZARD_STEPS = ['Choose Plan', 'Set Up Store', 'All Done'];

const WizardStepBar = ({ current }) => (
  <div className="owiz-steps">
    {WIZARD_STEPS.map((label, idx) => {
      const stepNum = idx + 1;
      const isDone = stepNum < current;
      const isActive = stepNum === current;
      return (
        <React.Fragment key={label}>
          {idx > 0 && <div className={`owiz-step-line${isDone ? ' done' : ''}`} />}
          <div className={`owiz-step${isActive ? ' active' : ''}${isDone ? ' done' : ''}`}>
            <div className="owiz-step-dot">{isDone ? '✓' : stepNum}</div>
            <div className="owiz-step-label">{label}</div>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

// ── Step 1: Choose Plan ───────────────────────────────────────────────────
const Step1Plan = ({ user, onNext }) => {
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'cancelled') setCancelled(true);
  }, []);

  const handleContinue = async () => {
    setError('');
    setBusy(true);
    try {
      const origin = window.location.origin;
      const successUrl = `${origin}/portal?billing=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${origin}/portal?billing=cancelled`;
      const result = await createBillingCheckoutSession({ interval: selectedPlan, successUrl, cancelUrl });
      if (!result?.url) throw new Error('Missing checkout URL.');
      window.location.href = result.url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout.');
      setBusy(false);
    }
  };

  return (
    <div className="owiz-body">
      <h2>Choose a Plan</h2>
      <p className="owiz-subtitle">Pick a subscription to get started with Servsolo.</p>

      {cancelled && (
        <div className="owiz-notice">
          Your checkout was cancelled. You can choose a plan and try again whenever you're ready.
        </div>
      )}

      <div className="owiz-plans">
        <button
          type="button"
          className={`owiz-plan${selectedPlan === 'monthly' ? ' active' : ''}`}
          onClick={() => setSelectedPlan('monthly')}
        >
          <div className="owiz-plan-name">Monthly</div>
          <div className="owiz-plan-price">AUD 99<span>/mo</span></div>
          <div className="owiz-plan-desc">Billed each month. Cancel anytime.</div>
        </button>

        <button
          type="button"
          className={`owiz-plan${selectedPlan === 'annual' ? ' active' : ''}`}
          onClick={() => setSelectedPlan('annual')}
        >
          <div className="owiz-plan-name">
            Annual
            <span className="owiz-plan-badge">Best value</span>
          </div>
          <div className="owiz-plan-price">AUD 948<span>/yr</span></div>
          <div className="owiz-plan-desc">Only <strong>$79/month</strong> — save AUD 240 for the year!</div>
        </button>
      </div>

      {error && <div className="owiz-error">{error}</div>}

      <button
        type="button"
        className="owiz-btn-primary"
        disabled={busy}
        onClick={handleContinue}
      >
        {busy ? 'Redirecting…' : 'Continue to Payment'}
      </button>

      <p className="owiz-fine-print">
        Signed in as {user.email} ·{' '}
        <button type="button" className="owiz-link-btn" onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
      </p>
    </div>
  );
};

// ── Step 2: Set Up Store ──────────────────────────────────────────────────
const EMPTY_WIZARD_FORM = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  phone: '',
  storeName: '',
  industry: '',
};

const Step2Store = ({ user, onNext }) => {
  const [form, setForm] = useState(() => ({
    ...EMPTY_WIZARD_FORM,
    firstName: user.user_metadata?.given_name || '',
    lastName: user.user_metadata?.family_name || '',
  }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const setField = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setFieldErrors(e => { const n = { ...e }; delete n[key]; return n; });
    setSubmitError('');
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'First Name is required.';
    if (!form.lastName.trim()) errs.lastName = 'Last Name is required.';
    if (!form.dateOfBirth) {
      errs.dateOfBirth = 'Date of Birth is required.';
    } else {
      const age = getAge(form.dateOfBirth);
      if (age === null) errs.dateOfBirth = 'Enter a valid Date of Birth.';
      else if (age < 18) errs.dateOfBirth = 'You must be 18 years or older.';
    }
    if (!form.phone.trim()) errs.phone = 'Phone Number is required.';
    if (!form.storeName.trim()) errs.storeName = 'Store Name is required.';
    if (!form.industry) errs.industry = 'Industry is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSubmitError('Please fix the highlighted fields before continuing.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitStoreAccessRequest({
        requested_by_user_id: user.id,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        date_of_birth: form.dateOfBirth,
        email: user.email,
        phone: form.phone.trim(),
        store_name: form.storeName.trim(),
        industry: form.industry,
      });

      notifyAdmin({
        kind: 'store-request',
        email: user.email,
        name: `${form.firstName.trim()} ${form.lastName.trim()}`,
        subject: `New store request: ${form.storeName.trim()}`,
        message: `Industry: ${form.industry}\nPhone: ${form.phone.trim()}\nRequested from: onboarding wizard`,
        metadata: { storeName: form.storeName.trim(), industry: form.industry, source: 'onboarding-wizard' },
      }).catch(() => {});

      onNext({ storeName: form.storeName.trim() });
    } catch (err) {
      const msg = err.message || 'Failed to submit request.';
      if (msg.toLowerCase().includes('duplicate key') || msg.toLowerCase().includes('pending_email')) {
        setSubmitError('A store request for this email already exists. Please wait for approval or contact support.');
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="owiz-body">
      <h2>Set Up Your Store</h2>
      <p className="owiz-subtitle">Tell us about your business so we can set up your store.</p>

      <form onSubmit={handleSubmit} noValidate>
        <div className="owiz-form-grid two">
          <div className="owiz-field">
            <label>First Name <span className="owiz-required">*</span></label>
            <input
              className={fieldErrors.firstName ? 'error' : ''}
              value={form.firstName}
              onChange={e => setField('firstName', e.target.value)}
              placeholder="Jane"
            />
            {fieldErrors.firstName && <small>{fieldErrors.firstName}</small>}
          </div>
          <div className="owiz-field">
            <label>Last Name <span className="owiz-required">*</span></label>
            <input
              className={fieldErrors.lastName ? 'error' : ''}
              value={form.lastName}
              onChange={e => setField('lastName', e.target.value)}
              placeholder="Smith"
            />
            {fieldErrors.lastName && <small>{fieldErrors.lastName}</small>}
          </div>
        </div>

        <div className="owiz-form-grid two">
          <div className="owiz-field">
            <label>Date of Birth <span className="owiz-required">*</span></label>
            <input
              type="date"
              className={fieldErrors.dateOfBirth ? 'error' : ''}
              value={form.dateOfBirth}
              onChange={e => setField('dateOfBirth', e.target.value)}
            />
            {fieldErrors.dateOfBirth && <small>{fieldErrors.dateOfBirth}</small>}
          </div>
          <div className="owiz-field">
            <label>Phone Number <span className="owiz-required">*</span></label>
            <input
              className={fieldErrors.phone ? 'error' : ''}
              value={form.phone}
              onChange={e => setField('phone', e.target.value)}
              placeholder="+61 400 000 000"
            />
            {fieldErrors.phone && <small>{fieldErrors.phone}</small>}
          </div>
        </div>

        <div className="owiz-form-grid two">
          <div className="owiz-field">
            <label>Store Name <span className="owiz-required">*</span></label>
            <input
              className={fieldErrors.storeName ? 'error' : ''}
              value={form.storeName}
              onChange={e => setField('storeName', e.target.value)}
              placeholder="e.g. Ali's Kebab House"
            />
            {fieldErrors.storeName && <small>{fieldErrors.storeName}</small>}
          </div>
          <div className="owiz-field">
            <label>Industry <span className="owiz-required">*</span></label>
            <select
              className={fieldErrors.industry ? 'error' : ''}
              value={form.industry}
              onChange={e => setField('industry', e.target.value)}
            >
              <option value="">Select industry</option>
              {INDUSTRY_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {fieldErrors.industry && <small>{fieldErrors.industry}</small>}
          </div>
        </div>

        <div className="owiz-field owiz-field-static">
          <label>Email</label>
          <div className="owiz-static-email">{user.email}</div>
        </div>

        {submitError && <div className="owiz-error">{submitError}</div>}

        <button type="submit" className="owiz-btn-primary" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Store Request'}
        </button>
      </form>

      <p className="owiz-fine-print">
        Signed in as {user.email} ·{' '}
        <button type="button" className="owiz-link-btn" onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
      </p>
    </div>
  );
};

// ── Step 3: All Done ──────────────────────────────────────────────────────
const Step3Done = ({ user, storeName, existingRequest }) => {
  const status = existingRequest?.status;

  return (
    <div className="owiz-body owiz-done">
      {status === 'approved' ? (
        <>
          <div className="owiz-done-icon owiz-done-icon--approved">✓</div>
          <h2>Store Approved!</h2>
          <p>Check your email for setup instructions to access your store.</p>
        </>
      ) : status === 'rejected' ? (
        <>
          <div className="owiz-done-icon owiz-done-icon--rejected">✕</div>
          <h2>Request Not Approved</h2>
          <p>
            Unfortunately your request for <strong>{existingRequest?.store_name || storeName || 'your store'}</strong>{' '}
            was not approved. Please contact our support team for more information.
          </p>
        </>
      ) : (
        <>
          <div className="owiz-done-icon">✓</div>
          <h2>Request Submitted!</h2>
          <p>
            We've received your request for{' '}
            <strong>{existingRequest?.store_name || storeName || 'your store'}</strong>.
            Our team will review it and send you an email invitation within 1–2 business days.
          </p>
        </>
      )}

      <SupportMessageModal
        user={user}
        source="portal-onboarding"
        triggerLabel="Message Servsolo"
        triggerClassName="owiz-btn-secondary"
      />

      <p className="owiz-fine-print">
        Signed in as {user.email} ·{' '}
        <button type="button" className="owiz-link-btn" onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
      </p>
    </div>
  );
};

// ── OnboardingWizard ──────────────────────────────────────────────────────
const OnboardingWizard = ({ user }) => {
  // step: 0 = loading, 1 = plan, 2 = store setup, 3 = done
  const [step, setStep] = useState(0);
  const [storeName, setStoreName] = useState('');
  const [existingRequest, setExistingRequest] = useState(null);

  useEffect(() => {
    let active = true;

    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const billingState = params.get('billing');
      const sessionId = params.get('session_id');

      // Clean the URL immediately
      window.history.replaceState({}, '', window.location.pathname);

      // 1. Check for existing store requests
      const { data: requests } = await supabase
        .from('store_access_requests')
        .select('id, store_name, industry, status, created_at')
        .eq('email', user.email)
        .order('created_at', { ascending: false });

      if (!active) return;

      if (requests && requests.length > 0) {
        setExistingRequest(requests[0]);
        setStep(3);
        return;
      }

      // 2. Handle billing=success — sync then go to step 2
      if (billingState === 'success' && sessionId) {
        try {
          await syncBillingFromCheckoutSession(sessionId);
        } catch {
          // sync failure is non-fatal; proceed to step 2 anyway
        }
        if (active) setStep(2);
        return;
      }

      // 3. Handle billing=cancelled — stay on step 1 (notice shown inside Step1Plan)
      if (billingState === 'cancelled') {
        if (active) setStep(1);
        return;
      }

      // 4. Check existing subscription
      try {
        const billing = await getMyBillingSubscription();
        if (!active) return;
        const isActive = ['active', 'trialing'].includes(String(billing?.status || '').toLowerCase());
        setStep(isActive ? 2 : 1);
      } catch {
        if (active) setStep(1);
      }
    };

    init();
    return () => { active = false; };
  }, [user.email]); // eslint-disable-line react-hooks/exhaustive-deps

  if (step === 0) {
    return (
      <div className="owiz-wrap">
        <div className="owiz-card">
          <div className="owiz-loading">Setting up your account…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="owiz-wrap">
      <div className="owiz-card">
        <div className="owiz-header">
          Serv<span className="owiz-brand-accent">Solo</span>
        </div>

        <WizardStepBar current={step} />

        {step === 1 && (
          <Step1Plan user={user} onNext={() => setStep(2)} />
        )}
        {step === 2 && (
          <Step2Store
            user={user}
            onNext={({ storeName: name }) => { setStoreName(name); setStep(3); }}
          />
        )}
        {step === 3 && (
          <Step3Done user={user} storeName={storeName} existingRequest={existingRequest} />
        )}
      </div>
    </div>
  );
};

// ── Overview ───────────────────────────────────────────────────────────────
const OverviewPage = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loadingReq, setLoadingReq] = useState(true);

  useEffect(() => {
    supabase
      .from('store_access_requests')
      .select('id, store_name, industry, status, created_at')
      .eq('email', user.email)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setRequests(data || []); setLoadingReq(false); });
  }, [user.email]);

  const statusBadge = (s) => {
    const map = { pending: 'sop-badge-pending', approved: 'sop-badge-approved', rejected: 'sop-badge-rejected' };
    return <span className={`sop-badge ${map[s] || 'sop-badge-pending'}`}>{s}</span>;
  };

  return (
    <div className="sop-content">
      <div className="sop-welcome">
        <h1>Welcome to Servsolo</h1>
        <p>Signed in as <strong>{user.email}</strong></p>
      </div>

      {/* Store request status */}
      {!loadingReq && requests.length > 0 && (
        <section className="sop-section">
          <h2>Your Store Requests</h2>
          <div className="sop-request-list">
            {requests.map(r => (
              <div key={r.id} className="sop-request-card">
                <div className="sop-request-name">{r.store_name || 'Unnamed store'}</div>
                <div className="sop-request-meta">{r.industry} · Submitted {fmtDate(r.created_at)}</div>
                {statusBadge(r.status)}
                {r.status === 'pending' && (
                  <p className="sop-request-note">Our team is reviewing your request. We'll email you once approved.</p>
                )}
                {r.status === 'approved' && (
                  <p className="sop-request-note">Your store has been approved! Check your email for setup instructions.</p>
                )}
                {r.status === 'rejected' && (
                  <p className="sop-request-note">This request was not approved. Send us a message if you have questions.</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Request form */}
      <section className="sop-section">
        <StoreRequestForm
          title={requests.length > 0 ? 'Request another store' : 'Request your first store'}
          subtitle="Send your business details to Servsolo admin. Once approved you'll receive an invitation to set up your store."
          submitLabel="Request Store Creation"
          user={user}
          compact
        />
      </section>

      {/* Quick actions */}
      <section className="sop-section sop-actions-row">
        <SupportMessageModal user={user} source="portal" triggerLabel="Message Servsolo" triggerClassName="sop-btn-secondary" />
      </section>
    </div>
  );
};

// ── Shared attachment renderer ─────────────────────────────────────────────
const MAX_FOLLOW_FILES = 10;

const AttachmentList = ({ attachments }) => {
  if (!attachments?.length) return null;
  return (
    <div className="sop-attachment-section">
      <div className="sop-attachment-section-head">
        <span className="sop-attachment-section-title">Attachments</span>
      </div>
      <div className="sop-attachments">
      {attachments.map((a, i) => (
        <div key={i} className="sop-attachment-card">
          {/\.(png|jpe?g|gif|webp)$/i.test(a.fileName || '') ? (
            <div className="sop-attachment-thumb-wrap">
              <a href={a.url} target="_blank" rel="noreferrer" className="sop-attachment-thumb-link">
                <img src={a.url} alt={a.fileName} className="sop-attachment-img" />
              </a>
              <button
                type="button"
                className="sop-attachment-download-icon"
                aria-label={`Download ${a.fileName || 'attachment'}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  downloadAttachment(a);
                }}
              >
                ↓
              </button>
            </div>
          ) : (
            <a href={a.url} target="_blank" rel="noreferrer" className="sop-attachment-file-tile">
              {(a.fileName || 'FILE').split('.').pop()?.toUpperCase() || 'FILE'}
            </a>
          )}
          <div className="sop-attachment-meta">
            <div className="sop-attachment-name" title={a.fileName || 'Attachment'}>{a.fileName || 'Attachment'}</div>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
};

// ── Messages ───────────────────────────────────────────────────────────────
const MessagesPage = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyFiles, setReplyFiles] = useState({});
  const [replyPreviews, setReplyPreviews] = useState({});
  const [replySubmitting, setReplySubmitting] = useState({});
  const [replyError, setReplyError] = useState({});

  const fetchMessages = () =>
    supabase
      .from('support_messages')
      .select('*')
      .eq('created_by_user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setMessages(data || []); setLoading(false); });

  useEffect(() => {
    setLoading(true);
    fetchMessages();

    const channel = supabase
      .channel(`user-messages-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_messages', filter: `created_by_user_id=eq.${user.id}` }, fetchMessages)
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addReplyFiles = (msgId, selected) => {
    const cur = replyFiles[msgId] || [];
    const merged = [...cur, ...Array.from(selected)].slice(0, MAX_FOLLOW_FILES);
    setReplyFiles(f => ({ ...f, [msgId]: merged }));
    setReplyPreviews(p => ({ ...p, [msgId]: merged.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null) }));
  };

  const removeReplyFile = (msgId, idx) => {
    const next = (replyFiles[msgId] || []).filter((_, i) => i !== idx);
    setReplyFiles(f => ({ ...f, [msgId]: next }));
    setReplyPreviews(p => ({ ...p, [msgId]: next.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null) }));
  };

  const handleSendReply = async (msgId) => {
    const text = (replyDrafts[msgId] || '').trim();
    if (!text) return;
    setReplySubmitting(s => ({ ...s, [msgId]: true }));
    setReplyError(e => ({ ...e, [msgId]: '' }));
    try {
      const files = replyFiles[msgId] || [];
      const attachments = files.length > 0 ? await Promise.all(files.map(f => uploadSupportAttachment(f))) : [];
      const name = user.user_metadata?.name || user.user_metadata?.full_name || user.email || 'You';
      await appendThreadMessage(msgId, { role: 'user', text, attachments, name });
      setReplyDrafts(d => ({ ...d, [msgId]: '' }));
      setReplyFiles(f => ({ ...f, [msgId]: [] }));
      setReplyPreviews(p => ({ ...p, [msgId]: [] }));
    } catch (err) {
      setReplyError(e => ({ ...e, [msgId]: err.message || 'Failed to send.' }));
    } finally {
      setReplySubmitting(s => ({ ...s, [msgId]: false }));
    }
  };

  if (loading) return <div className="sop-content"><p className="sop-loading">Loading messages…</p></div>;

  const renderBubble = (label, text, attachments, timestamp) => (
    <div className={`sop-message-bubble ${label === 'You' ? 'sop-bubble-user' : 'sop-bubble-admin'}`}>
      <div className="sop-bubble-label">{label}{timestamp ? ` · ${fmtDate(timestamp)}` : ''}</div>
      <p>{text}</p>
      <AttachmentList attachments={attachments} />
    </div>
  );

  return (
    <div className="sop-content">
      <div className="sop-section-header">
        <h1>Messages</h1>
        <SupportMessageModal user={user} source="portal" triggerLabel="+ New Message" triggerClassName="sop-btn-primary" />
      </div>

      {messages.length === 0 ? (
        <div className="sop-empty">
          <p>No messages yet.</p>
          <SupportMessageModal user={user} source="portal" triggerLabel="Send your first message" triggerClassName="sop-btn-secondary" />
        </div>
      ) : (
        <div className="sop-message-list">
          {messages.map(m => {
            const { text, attachments } = parseContent(m.message);
            const adminReply = m.admin_reply ? parseContent(m.admin_reply) : null;
            const thread = Array.isArray(m.thread_messages) ? m.thread_messages : [];
            const isOpen = expanded === m.id;
            const canReply = !['archived', 'resolved'].includes(m.status);
            const allAttachments = collectMessageAttachments({ attachments }, adminReply, thread);
            return (
              <div key={m.id} className={`sop-message-card${isOpen ? ' open' : ''}`}>
                <div className="sop-message-header" onClick={() => setExpanded(isOpen ? null : m.id)}>
                  <div className="sop-message-subject">{m.subject}</div>
                  <div className="sop-message-meta">
                    {fmtDate(m.created_at)}
                    <span className={`sop-badge sop-badge-${m.status}`}>{m.status.replace('_', ' ')}</span>
                    {allAttachments.length > 0 && <span className="sop-attachment-indicator" title={`${allAttachments.length} attachment${allAttachments.length === 1 ? '' : 's'}`}>📎 {allAttachments.length}</span>}
                    {m.admin_reply && <span className="sop-replied-dot" title="Admin replied">● Reply</span>}
                  </div>
                  <span className="sop-chevron">{isOpen ? '▲' : '▼'}</span>
                </div>
                {isOpen && (
                  <div className="sop-message-body">
                    {allAttachments.length > 1 && (
                      <div className="sop-thread-actions">
                        <button type="button" className="sop-download-all-btn" onClick={() => downloadAttachments(allAttachments)}>Download all attachments</button>
                      </div>
                    )}
                    {/* Original message */}
                    {renderBubble('You', text, attachments)}

                    {/* Original admin reply (if set before thread existed) */}
                    {adminReply && thread.length === 0 && renderBubble('Servsolo', adminReply.text, adminReply.attachments, m.admin_replied_at)}

                    {/* Thread messages */}
                    {thread.map((entry, i) => {
                      const isUser = entry.role === 'user';
                      return renderBubble(
                        isUser ? 'You' : 'Servsolo',
                        entry.text,
                        entry.attachments || [],
                        entry.created_at
                      );
                    })}

                    {/* Awaiting if no reply at all */}
                    {!adminReply && thread.filter(t => t.role === 'admin').length === 0 && (
                      <div className="sop-message-awaiting">Awaiting reply from Servsolo team…</div>
                    )}

                    {/* User follow-up input */}
                    {canReply && (
                      <div className="sop-follow-up">
                        <textarea
                          className="sop-follow-up-input"
                          rows={3}
                          placeholder="Send a follow-up message…"
                          value={replyDrafts[m.id] || ''}
                          onChange={e => setReplyDrafts(d => ({ ...d, [m.id]: e.target.value }))}
                        />
                        <label className="sop-follow-up-file-btn">
                          + Attach files
                          <input
                            type="file" multiple style={{ display: 'none' }}
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                            disabled={(replyFiles[m.id] || []).length >= MAX_FOLLOW_FILES}
                            onChange={e => addReplyFiles(m.id, e.target.files)}
                          />
                        </label>
                        {(replyFiles[m.id] || []).length > 0 && (
                          <div className="sop-follow-up-thumbs">
                            {(replyFiles[m.id] || []).map((f, i) => (
                              <div key={i} className="sop-follow-up-thumb">
                                {(replyPreviews[m.id] || [])[i]
                                  ? <img src={(replyPreviews[m.id] || [])[i]} alt={f.name} />
                                  : <div className="sop-follow-up-thumb-file">{f.name.split('.').pop().toUpperCase()}</div>
                                }
                                <button type="button" className="sop-follow-up-thumb-remove" onClick={() => removeReplyFile(m.id, i)}>×</button>
                                <div className="sop-follow-up-thumb-name">{f.name}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {replyError[m.id] && <div className="sop-follow-up-error">{replyError[m.id]}</div>}
                        <div className="sop-follow-up-actions">
                          <button
                            type="button"
                            className="sop-btn-primary"
                            disabled={replySubmitting[m.id] || !(replyDrafts[m.id] || '').trim()}
                            onClick={() => handleSendReply(m.id)}
                          >
                            {replySubmitting[m.id] ? 'Sending…' : 'Send Follow-up'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Account ────────────────────────────────────────────────────────────────
const AccountPage = ({ user }) => {
  const handleSignOut = () => supabase.auth.signOut();
  const name = user.user_metadata?.name || user.user_metadata?.full_name || '';
  const phone = user.user_metadata?.phone || '';
  const provider = user.app_metadata?.provider || 'email';
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingAction, setBillingAction] = useState('');
  const [billingError, setBillingError] = useState('');

  useEffect(() => {
    let active = true;

    const loadBilling = async () => {
      try {
        const result = await getMyBillingSubscription();
        if (active) setBilling(result || null);
      } catch (error) {
        if (active) setBillingError(error.message || 'Failed to load billing status.');
      } finally {
        if (active) setBillingLoading(false);
      }
    };

    const search = new URLSearchParams(window.location.search);
    const billingState = search.get('billing');
    const sessionId = search.get('session_id');

    const syncIfNeeded = async () => {
      if (billingState !== 'success' || !sessionId) {
        await loadBilling();
        return;
      }

      try {
        setBillingAction('sync');
        await syncBillingFromCheckoutSession(sessionId);
        const next = await getMyBillingSubscription();
        if (active) setBilling(next || null);
        const cleanUrl = `${window.location.pathname}`;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (error) {
        if (active) setBillingError(error.message || 'Subscription sync failed.');
      } finally {
        if (active) {
          setBillingLoading(false);
          setBillingAction('');
        }
      }
    };

    syncIfNeeded();

    return () => {
      active = false;
    };
  }, []);

  const startSubscription = async (interval) => {
    setBillingError('');
    setBillingAction(interval);
    try {
      const origin = window.location.origin;
      const successUrl = `${origin}/portal/account?billing=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${origin}/portal/account?billing=cancelled`;
      const result = await createBillingCheckoutSession({ interval, successUrl, cancelUrl });
      if (!result?.url) throw new Error('Missing checkout URL.');
      window.location.href = result.url;
    } catch (error) {
      setBillingError(error.message || 'Failed to start checkout.');
      setBillingAction('');
    }
  };

  const openBillingPortal = async () => {
    setBillingError('');
    setBillingAction('portal');
    try {
      const result = await createBillingPortalSession(`${window.location.origin}/portal/account`);
      if (!result?.url) throw new Error('Missing billing portal URL.');
      window.location.href = result.url;
    } catch (error) {
      setBillingError(error.message || 'Failed to open billing portal.');
      setBillingAction('');
    }
  };

  const isActive = ['active', 'trialing'].includes(String(billing?.status || '').toLowerCase());
  const planLabel = billing?.billing_interval === 'annual' ? 'Annual (AUD 948/year)' : 'Monthly (AUD 99/month)';

  return (
    <div className="sop-content">
      <h1>Account</h1>
      <div className="sop-account-card">
        <div className="sop-account-avatar">{(name || user.email || '?')[0].toUpperCase()}</div>
        <div className="sop-account-info">
          {name && <div className="sop-account-name">{name}</div>}
          <div className="sop-account-email">{user.email}</div>
          {phone && <div className="sop-account-phone">{phone}</div>}
          <div className="sop-account-provider">Signed in via {provider}</div>
        </div>
      </div>

      <div className="sop-account-card sop-billing-card">
        <div className="sop-account-info">
          <div className="sop-account-name">Subscription</div>
          {billingLoading ? (
            <div className="sop-account-email">Loading billing status...</div>
          ) : (
            <>
              <div className="sop-account-email">
                {billing?.status ? `Status: ${billing.status}` : 'Status: not subscribed'}
              </div>
              <div className="sop-account-phone">
                {billing?.billing_interval ? `Plan: ${planLabel}` : 'Choose a plan to activate your account billing.'}
              </div>
              {billing?.current_period_end && (
                <div className="sop-account-provider">
                  Current period ends {fmtDate(billing.current_period_end)}
                </div>
              )}
            </>
          )}

          <div className="sop-billing-actions">
            <button
              type="button"
              className="sop-btn-primary"
              disabled={billingAction !== ''}
              onClick={() => startSubscription('monthly')}
            >
              {billingAction === 'monthly' ? 'Redirecting...' : 'Subscribe Monthly'}
            </button>
            <button
              type="button"
              className="sop-btn-secondary"
              disabled={billingAction !== ''}
              onClick={() => startSubscription('annual')}
            >
              {billingAction === 'annual' ? 'Redirecting...' : 'Subscribe Annual'}
            </button>
            {isActive && (
              <button
                type="button"
                className="sop-btn-secondary"
                disabled={billingAction !== ''}
                onClick={openBillingPortal}
              >
                {billingAction === 'portal' ? 'Opening...' : 'Manage Billing'}
              </button>
            )}
          </div>

          {billingError && <div className="sop-follow-up-error">{billingError}</div>}
        </div>
      </div>
      <button className="sop-btn-danger" onClick={handleSignOut}>Sign Out</button>
    </div>
  );
};

// ── Shell ──────────────────────────────────────────────────────────────────
const StoreOwnerPortal = ({ user, session, page = 'overview', onboardingOnly = false }) => {
  const navigate = useNavigate();
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ss_theme') === 'dark');
  const [branding, setBranding] = useState(() => {
    try {
      const cached = localStorage.getItem('ss_public_site_content_v2');
      if (cached) return buildPublicSiteContentMap(JSON.parse(cached))?.branding || {};
    } catch {}
    return {};
  });
  useEffect(() => {
    fetchPublicSiteContent()
      .then(rows => {
        try { localStorage.setItem('ss_public_site_content_v2', JSON.stringify(rows)); } catch {}
        setBranding(buildPublicSiteContentMap(rows)?.branding || {});
      })
      .catch(() => {});
  }, []);
  const handleSignOut = async () => { await supabase.auth.signOut(); navigate('/'); };
  const toggleTheme = () => setDarkMode(d => { const next = !d; localStorage.setItem('ss_theme', next ? 'dark' : 'light'); return next; });

  useEffect(() => {
    let active = true;
    let running = false;

    const ensureSessionStillValid = async () => {
      if (!active || running) return;
      running = true;
      try {
        const { data, error } = await supabase.auth.getUser();
        if (!active) return;
        if (error || !data?.user?.id) {
          await supabase.auth.signOut();
          navigate('/login', { replace: true });
        }
      } finally {
        running = false;
      }
    };

    ensureSessionStillValid();
    const timer = window.setInterval(ensureSessionStillValid, 60000);
    window.addEventListener('pointerdown', ensureSessionStillValid);

    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('pointerdown', ensureSessionStillValid);
    };
  }, [navigate]);

  const navItems = [
    { label: 'Overview', page: 'overview', path: '/portal' },
    { label: 'Messages', page: 'messages', path: '/portal/messages' },
    { label: 'Account', page: 'account', path: '/portal/account' },
  ];

  return (
    <div className={`sop-layout${sidebarHidden ? ' sop-fullscreen' : ''}${darkMode ? ' dark' : ''}${onboardingOnly ? ' sop-no-sidebar' : ''}`}>
      {onboardingOnly && (
        <div className="sop-onboarding-topbar">
          <a href="/" className="sop-onboarding-home-link">← Servsolo Home</a>
          <button type="button" className="sop-onboarding-signout-btn" onClick={handleSignOut}>Sign Out</button>
        </div>
      )}
      {!onboardingOnly && (
        <button
          className="sop-toggle-btn"
          onClick={() => setSidebarHidden(h => !h)}
          title={sidebarHidden ? 'Show navigation' : 'Fullscreen'}
        >
          {sidebarHidden ? '⌟' : '⌜'}
        </button>
      )}
      {!onboardingOnly && !sidebarHidden && (
        <aside className="sop-sidebar">
          <div className="sop-brand-row">
            <div className="sop-brand">
              {branding?.logoImageUrl
                ? <img src={branding.logoImageUrl} alt="Servsolo" className="sop-brand-logo" style={{ width: Math.min(Number(branding.logoWidthDesktop) || 140, 140) + 'px' }} />
                : <>Serv<span className="sop-brand-accent">Solo</span></>}
            </div>
            <button className="sop-theme-toggle" onClick={toggleTheme} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              {darkMode ? '☀' : '☽'}
            </button>
          </div>
          <div className="sop-role-label">User</div>
          <nav className="sop-nav">
            {navItems.map(item => (
              <Link key={item.page} to={item.path} className={`sop-nav-link${page === item.page ? ' active' : ''}`}>
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="sop-sidebar-footer">
            <div className="sop-sidebar-email">{user.email}</div>
            <a href="/" className="sop-site-link">Back to Servsolo Home</a>
            <button className="sop-signout-btn" onClick={handleSignOut}>Sign Out</button>
          </div>
        </aside>
      )}
      <main className="sop-main">
        {page === 'onboarding' && <OnboardingWizard user={user} />}
        {page === 'overview' && <OverviewPage user={user} session={session} />}
        {page === 'messages' && <MessagesPage user={user} session={session} />}
        {page === 'account' && <AccountPage user={user} />}
      </main>
    </div>
  );
};

export default StoreOwnerPortal;
